import { useSentryMutation, useSentryQuery } from '../utils/sentryQueryWrapper';
import {
  AdvanceLedgerFilters,
  fetchAdvanceBatchByUID,
  fetchAdvanceBatchOrders,
  fetchAdvanceBatches,
  fetchAdvanceCodSummary,
  fetchAdvanceCodTransfers,
  fetchAdvanceLedger,
  requestAdvanceCodActivation,
} from '@/api/advanceCodService';
import {
  AdvanceBatchDetailResponse,
  AdvanceBatchFilters,
  AdvanceBatchListResponse,
  AdvanceBatchOrdersResponse,
  AdvanceCodActivationRequestResponse,
  AdvanceCodSummaryResponse,
  ErrorMessageResponse,
  FfcTransfersResponse,
  TranslationListResponse,
} from '@/types';

/**
 * QA defect 15: the app sets refetchOnWindowFocus:false globally and the
 * disbursement toast needs to OBSERVE an approved→disbursed transition —
 * so batch queries poll modestly while an advance-cod page is mounted
 * (react-query intervals only run for mounted queries).
 */
const BATCH_POLL_INTERVAL_MS = 60000;

// Financing summary (credit limits + program state)
export const useFetchAdvanceCodSummary = (enabled: boolean = true) =>
  useSentryQuery<AdvanceCodSummaryResponse, ErrorMessageResponse>(
    ['advance-cod-summary'],
    () => fetchAdvanceCodSummary(),
    // 60s poll (only while mounted) so webhook-driven state changes —
    // suspension, limit updates, disbursements — reach a stationary page
    // without navigation, matching the batches queries' cadence.
    { enabled, staleTime: 60000, refetchInterval: 60000 },
  );

// Request Advance COD activation (R1). Silent error mode ('' default message):
// the promo card surfaces its own inline error / 403-hide handling, so no
// global toast on failure.
export const useRequestAdvanceCodActivation = () =>
  useSentryMutation<AdvanceCodActivationRequestResponse, ErrorMessageResponse, void>(
    () => requestAdvanceCodActivation(),
    { defaultErrorMessage: '' },
  );

// Advance batches list with pagination + filters
export const useFetchAdvanceBatches = (
  page: number,
  pageSize?: number,
  filters?: AdvanceBatchFilters,
  enabled: boolean = true,
) =>
  useSentryQuery<AdvanceBatchListResponse, ErrorMessageResponse>(
    [
      'advance-cod-batches',
      page,
      filters?.filter_status,
      filters?.filter_created_at_from,
      filters?.filter_created_at_to,
      filters?.filter_platform_code,
    ],
    () => fetchAdvanceBatches(page, pageSize, filters),
    { enabled, refetchInterval: BATCH_POLL_INTERVAL_MS },
  );

// Single advance batch by UID (detail + settlements + order counts)
export const useFetchAdvanceBatchByUID = (uid: string) =>
  useSentryQuery<AdvanceBatchDetailResponse, ErrorMessageResponse>(
    ['advance-cod-batch', uid],
    () => fetchAdvanceBatchByUID(uid),
    { enabled: !!uid, refetchInterval: BATCH_POLL_INTERVAL_MS },
  );

// Paginated financed orders of one batch (optionally excluded-only)
export const useFetchAdvanceBatchOrders = (
  uid: string,
  page: number,
  pageSize?: number,
  excludedOnly?: boolean,
  enabled: boolean = true,
) =>
  useSentryQuery<AdvanceBatchOrdersResponse, ErrorMessageResponse>(
    ['advance-cod-batch-orders', uid, page, pageSize, !!excludedOnly],
    () => fetchAdvanceBatchOrders(uid, page, pageSize, excludedOnly),
    { enabled: enabled && !!uid },
  );

// FFC settlement transfers list ("Transfers to FFC", W6)
export const useFetchAdvanceCodTransfers = (
  page: number,
  pageSize?: number,
  enabled: boolean = true,
) =>
  useSentryQuery<FfcTransfersResponse, ErrorMessageResponse>(
    ['advance-cod-transfers', page, pageSize],
    () => fetchAdvanceCodTransfers(page, pageSize),
    { enabled },
  );

// FFC mirror-ledger page (server-filtered /merchants/transactions)
export const useFetchAdvanceLedger = (
  page: number,
  pageSize?: number,
  filters?: AdvanceLedgerFilters,
) =>
  useSentryQuery<TranslationListResponse, ErrorMessageResponse>(
    [
      'advance-cod-ledger',
      page,
      filters?.filter_subject,
      filters?.filter_created_at_from,
      filters?.filter_created_at_to,
    ],
    () => fetchAdvanceLedger(page, pageSize, filters),
  );
